import React, { FC, useEffect, useState } from 'react';
import {
    Icon,
    SelectionMode,
    DetailsListLayoutMode,
    IconButton,
    IContextualMenuItem,
    TooltipHost,
    DirectionalHint,
} from '@fluentui/react';
import { Language, DetailsList, ColumnInfo, DatePicker, Label } from '@eta/components';
import { useTranslation } from 'react-i18next';
import { isNullOrUndefined } from 'util';
import { GetRepresentationOfTaxpayerDto } from '../../../Services/ClientProxy/clientProxy';

import './UsersGrid.scss';
import { authService } from '../../../Security/Security';
import {
    Icons,
    Actions,
    StatusCodes,
    Languages,
} from '../../../common/config/constants';
import { EditableItem } from './EditableItem';
import { UIHelpers, NumberOfShimmerLines } from '../../../helpers/UIHelpers';
import RepresentationClaims from '../RepresentationClaims';

interface UsersGridProps {
    currentLanguage: Language;
    data: GetRepresentationOfTaxpayerDto[];
    onActionClick: (
        action: string,
        item: GetRepresentationOfTaxpayerDto
    ) => void;
}
export const UsersGrid: FC<UsersGridProps> = (props: UsersGridProps) => {
    const { t } = useTranslation(['resources', 'myProfile', 'common']);
    const {
        currentLanguage: currentLanguageProps,
        data,
        onActionClick,
    } = props;

    const [columns, setColumns] = useState<ColumnInfo[]>();
    const [currentLanguage, setCurrentLanguage] = useState<Language>(
        currentLanguageProps
    );
    const [items, setItems] = useState<
        EditableItem<GetRepresentationOfTaxpayerDto>[]
    >();

    useEffect(() => {
        if (currentLanguage.lang !== currentLanguageProps.lang) {
            setCurrentLanguage(currentLanguageProps);
        }
    }, [currentLanguageProps, items, currentLanguage]);

    useEffect(() => {
        if (data?.length) {
            setItems(
                data?.map((item) => {
                    return new EditableItem(item, item, false);
                })
            );
        }
    }, [data, t]);
    
    const [pageIndex, setPageIndex] = useState<number>(0);
    const changeItem = (
        representationID: number | undefined,
        newItem: EditableItem<GetRepresentationOfTaxpayerDto>
    ) => {
        const representationItem = data?.find(
            (i) => i.taxpayerRepresentationID === representationID
        );
        if (representationItem) {
            const index = data?.indexOf(representationItem);
            const newItems = items?.slice() ?? [];
            if (!isNullOrUndefined(index) && index >= 0) {
                newItems[index] = { ...representationItem, ...newItem };
                setItems([...newItems]);
            }
        }
    };

    // const onEdit = (editItem: EditableItem<GetRepresentationOfTaxpayerDto>) => {
    //     changeItem(editItem.item.taxpayerRepresentationID, {
    //         ...editItem,
    //         isEdited: true,
    //     });
    // };

    const EditAction = (
        item: EditableItem<GetRepresentationOfTaxpayerDto>
    ): IContextualMenuItem => {
        return {
            key: 'Edit',
            text: t('form.actions.edit'),
            iconProps: { iconName: Icons.Edit },
            onClick: () => {
                // onEdit(item);
                onActionClick(Actions.EditUSER, item?.item);
            },
        };
    };

    const ViewDetailsAction = (
        item: GetRepresentationOfTaxpayerDto
    ): IContextualMenuItem => {
        return {
            key: 'ViewDetails',
            text: t('form.actions.ViewDetails'),
            iconProps: { iconName: Icons.View },
            onClick: () =>
                onActionClick && onActionClick(Actions.ViewDetails, item),
        };
    };

    const BlockAction = (
        item: GetRepresentationOfTaxpayerDto
    ): IContextualMenuItem => {
        return {
            key: 'Block',
            text: t('form.actions.block'),
            iconProps: { iconName: Icons.Block },
            onClick: () =>
                onActionClick && onActionClick(Actions.BlockUSER, item),
        };
    };

    const UnBlockAction = (
        item: GetRepresentationOfTaxpayerDto
    ): IContextualMenuItem => {
        return {
            key: 'Block',
            text: t('form.actions.unblock'),
            iconProps: { iconName: Icons.Block },
            onClick: () =>
                onActionClick && onActionClick(Actions.UnBlockUSER, item),
        };
    };

    const checkIsGreaterToday = (date: Date | undefined) =>
        date === undefined ||
        new Date(date.toDateString()) >= new Date(new Date().toDateString());

    const gridActions = (
        editItem: EditableItem<GetRepresentationOfTaxpayerDto>
    ): IContextualMenuItem[] => {
        const actions: IContextualMenuItem[] = [];
        const UserId = Number(authService.currentUser?.profile?.ProfId);
        actions.push(ViewDetailsAction(editItem.item));
        switch (editItem.item.status.toString()) {
            case StatusCodes.Active:
                if (
                    editItem.item.userProfileStatus.toString() ===
                    StatusCodes.Active
                ) {
                    if (UserId !== editItem.item.userProfileID &&
                        (checkIsGreaterToday(editItem.item.dateActivated) ||
                            checkIsGreaterToday(editItem.item.dateDeactivated))
                    ) {
                        actions.push(EditAction(editItem));
                    }
                    if (editItem.item.userProfileID.toString() !== authService.currentUser?.profile.ProfId)
                        actions.push(BlockAction(editItem.item));
                }
                break;
            case StatusCodes.InActive:
                if (checkIsGreaterToday(editItem.item.dateDeactivated)) {
                    actions.push(UnBlockAction(editItem.item));
                }
                break;
            default:
                break;
        }
        return actions;
    };

    const GetgridActions = (
        item: EditableItem<GetRepresentationOfTaxpayerDto>
    ) => {
        const actions = gridActions(item);
        return actions.length ? (
            <span>
                <IconButton
                    className="iconActions"
                    iconProps={{ iconName: Icons.MoreVertical }}
                    menuProps={{
                        items: actions,
                    }}
                />
            </span>
        ) : null;
    };

    const CheckProfileStatus = (status: string) => {
        return status === StatusCodes.Active
            ? t('constants.Allowed')
            : t('constants.BlockedAsUser');
    };

    useEffect(() => {
        const gridColumns = [
            {
                key: 'profileNamePrimaryLang',
                name: t('TaxpayerUserGrid.UserName'),
                fieldName: 'profileNamePrimaryLang',
                minWidth: 100,
                maxWidth: 200,
                isResizable: true,

                isSortedDescending: true,

                onRender: (
                    editItem: EditableItem<GetRepresentationOfTaxpayerDto>
                ) => {
                    const isEnLang = currentLanguage.lang
                        .trim()
                        .toLocaleLowerCase()
                        .startsWith(Languages.En);

                    let fieldName;
                    if (
                        (!isEnLang &&
                            editItem.item.profileNameSecondaryLang?.trim()) ||
                        (isEnLang &&
                            !editItem.item.profileNamePrimaryLang?.trim())
                    ) {
                        fieldName = editItem.item.profileNameSecondaryLang;
                    } else if (
                        (isEnLang &&
                            editItem.item.profileNamePrimaryLang?.trim()) ||
                        (!isEnLang &&
                            !editItem.item.profileNameSecondaryLang?.trim())
                    ) {
                        fieldName = editItem.item.profileNamePrimaryLang;
                    }
                    return (
                        <div className="horizontal">
                            <Icon iconName="erpIcon" />
                            <span>{fieldName}</span>
                        </div>
                    );
                },
            },
            {
                key: 'email',
                name: t('TaxpayerUserGrid.E-mail'),
                fieldName: 'email',
                minWidth: 200,
                maxWidth: 250,
                isRowHeader: true,
                isResizable: true,
                isSorted: false,
                isSortedDescending: false,
                sortAscendingAriaLabel: 'Sorted A to Z',
                sortDescendingAriaLabel: 'Sorted Z to A',
                data: 'string',
                isPadded: true,

                onRender: (
                    editItem: EditableItem<GetRepresentationOfTaxpayerDto>
                ) => {
                    return (
                        <div className="horizontal">
                            <span>{editItem.item.email}</span>
                        </div>
                    );
                },
            },
            {
                key: 'role',
                name: t('TaxpayerUserGrid.Role'),
                fieldName: 'role',
                minWidth: 70,
                maxWidth: 80,
                isRowHeader: true,
                isResizable: true,
                isSorted: false,
                isSortedDescending: false,
                sortAscendingAriaLabel: 'Sorted A to Z',
                sortDescendingAriaLabel: 'Sorted Z to A',
                data: 'string',
                isPadded: true,

                onRender: (
                    editItem: EditableItem<GetRepresentationOfTaxpayerDto>
                ) => {
                    const claimsCode = editItem.item.taxpayerRepresentationClaims?.map(x => x.claimCode ?? '');
                    return (
                        <span>
                            {`${t(editItem.item.role || '')} `}
                            <TooltipHost
                                directionalHint={DirectionalHint.rightBottomEdge}
                                tooltipProps={{
                                    onRenderContent: () => (
                                        <div className='contentToolGrid'>
                                            <Label className='roleLabelBold'>{t(editItem.item.role || '')}</Label>
                                            <br />
                                            <RepresentationClaims
                                                ClaimsCodes={claimsCode}
                                            />
                                        </div>
                                    ),
                                }}
                            >
                                <Icon iconName="Info" />
                            </TooltipHost>
                        </span>
                    );
                },
            },
            {
                key: 'allowedBranchesCount',
                name: t('TaxpayerUserGrid.NumberBranches'),
                fieldName: 'allowedBranchesCount',
                minWidth: 120,
                maxWidth: 130,
                isRowHeader: true,
                isResizable: true,
                isSorted: false,
                isSortedDescending: false,
                data: 'string',
                isPadded: true,

                onRender: (
                    editItem: EditableItem<GetRepresentationOfTaxpayerDto>
                ) => {
                    return (
                        <span>
                            {editItem.item.allBranches ? t('myProfile:allBranches') : editItem.item.allowedBranchesCount}
                        </span>
                    );
                },
            },
            {
                key: 'dateActivated',
                name: t('TaxpayerUserGrid.ActiveFrom'),
                fieldName: 'dateActivated',
                minWidth: 160,
                maxWidth: 170,
                isRowHeader: true,
                isResizable: true,

                onRender: (
                    editItem: EditableItem<GetRepresentationOfTaxpayerDto>
                ) => {
                    return (
                        <div>
                            {
                                (editItem.isEdited && checkIsGreaterToday(editItem?.item.dateActivated)) ?
                                    (
                                        <DatePicker
                                            placeholder={t('common:SelectDate')}
                                            firstWeekOfYear={1}
                                            showMonthPickerAsOverlay
                                            allowTextInput
                                            value={editItem.editedItem.dateActivated}
                                            onSelectDate={(val) => {
                                                const newItem = editItem;
                                                newItem.editedItem = editItem?.editedItem.clone();
                                                newItem.editedItem.dateActivated = val ?? new Date();
                                                changeItem(editItem?.item?.taxpayerRepresentationID, newItem);
                                            }}
                                            formatDate={(date) => { return UIHelpers.FormatDatePickerValue(date, currentLanguage?.langIso4); }}
                                            parseDateFromString={UIHelpers.ParseDatePickerString}
                                        />
                                    ) : (
                                        <span>
                                            {`${editItem.item.dateActivated ? UIHelpers.getLocalizedDateTime('Date', editItem?.item.dateActivated, currentLanguageProps.lang) : ''}
                                           - ${editItem.item.dateDeactivated ? UIHelpers.getLocalizedDateTime('Date', editItem?.item.dateDeactivated, currentLanguageProps.lang) : ''}`}
                                        </span>
                                    )
                            }

                        </div>
                    );
                },
            },
            {
                key: 'status',
                name: t('TaxpayerUserGrid.Activitystatus'),
                fieldName: 'status',
                minWidth: 70,
                maxWidth: 70,
                isResizable: true,
                data: 'string',
                isPadded: true,

                onRender: (
                    editItem: EditableItem<GetRepresentationOfTaxpayerDto>
                ) => {
                    return (
                        <span
                            className={
                                editItem.item.status.toString() ===
                                    StatusCodes.Active &&
                                    editItem.item.userProfileStatus.toString() ===
                                    StatusCodes.Active
                                    ? 'green'
                                    : 'red'
                            }
                        >
                            {editItem.item.status.toString() ===
                                StatusCodes.Active
                                ? CheckProfileStatus(
                                    editItem.item.userProfileStatus.toString()
                                )
                                : t('constants.Blocked')}
                        </span>
                    );
                },
            },
            {
                key: 'action',
                name: '',
                fieldName: '',
                minWidth: 70,
                maxWidth: 70,
                isRowHeader: true,
                isResizable: false,

                onRender: (
                    item: EditableItem<GetRepresentationOfTaxpayerDto>
                ) => {
                    // eslint-disable-next-line no-nested-ternary
                    return GetgridActions(item);
                },
            },
        ];
        setColumns(gridColumns);
    }, [currentLanguageProps, items]);

    return (
        <DetailsList
            className="grid userGrid"
            columns={columns ?? []}
            items={items}
            shimmerLines={NumberOfShimmerLines}
            enableShimmer={!items}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            labels={{
                resultPerPage: t('common:nbItemPerPage'),
                totalRecord: t('common:totalRecord'),
            }}
            noItemsPlaceholder={<span>{t('common:NoRecords')} </span>}
            totalItemCount={items?.length}
            paginationSetting={{
                nbItemPerPage: 10,
                nbPageShown: 3,
                initialPage: pageIndex,
                currentPage: pageIndex,
            }}
            onPageChanged={(index) => {
                setPageIndex(index);
                return false;
            }}
        />
    );
};
